import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, distinctUntilChanged, filter, combineLatest } from 'rxjs';

import { select, Store } from '@ngrx/store';
import { ApplicationState } from '../../store/application-store';
import { Brand } from '../models/brand';
import { BrandProvider } from './brand-provider';
import { getPreviewMode, selectIsMyDashboardMode } from '../../user-settings/selectors/user-settings';
import { selectDesignerMode } from '../../dashboard-designer/selectors/dashboard-designer';

@Injectable()
export class BrandService {

  private readonly isMyDashboardMode = this.store.pipe(
    select(selectIsMyDashboardMode),
    filter(x => x !== undefined));
  private readonly isInPreviewMode = this.store.pipe(
    select(getPreviewMode),
    filter(x => x !== undefined));
  private readonly isInDesignerMode = this.store.pipe(
    select(selectDesignerMode));

  /**
   * Emits true when the mojo brand should be used otherwise Customer brand should be used.
   **/
  useMojoBrand(): Observable<boolean> {
    const brandDataPipe = combineLatest([this.isBrandSwitchingEnabled(), this.isInPreviewMode, this.isInDesignerMode]);
    return brandDataPipe.pipe(
      map(([brandSwitchingEnabled, isInPreviewMode, isInDesignerMode]) => {
        return (brandSwitchingEnabled && !isInPreviewMode && !isInDesignerMode);
      }),
    );
  }

  /**
   * Emits true when brand switching is enabled
   * */
  isBrandSwitchingEnabled(): Observable<boolean> {
    return this.isMyDashboardMode.pipe();
  }

  /**
   * Emits true when current brand is mojo brand
   **/
  isMojoBrand(): Observable<boolean> {
    return this.getCurrentBrand().pipe(
      map((brand) => brand && brand.name === 'mojo')
    );
  }

  /*
   * Emits the current brand. This service manages the current brand via the useMojoBrand stream
   **/
  getCurrentBrand(): Observable<Brand> {
    return this.brandProvider.getCurrentBrand().pipe(distinctUntilChanged());
  }

  getCustomerBrandName(): Observable<string> {
    return this.brandProvider.getCustomerBrandName();
  }

  constructor(
    private readonly store: Store<ApplicationState>,
    private readonly brandProvider: BrandProvider
  ) { }

}
